import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { Mic, Settings, Users, Phone, BarChart2, HelpCircle, Bot, AlertCircle, Clock, Calendar, RefreshCw } from 'lucide-react';
import AIVoiceAgentTroubleshooting from './AIVoiceAgentTroubleshooting';
import VoiceAgentStatusWidget from './VoiceAgentStatusWidget';

const AIVoiceAgentDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Simulate loading and check for potential errors
  useEffect(() => {
    const loadDashboard = async () => {
      try {
        // Simulate API call or data loading
        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load voice agent dashboard:", error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    loadDashboard();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-gray-600">Loading voice agent dashboard...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <AlertCircle className="h-6 w-6 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-medium text-red-800">Error Loading Dashboard</h3>
            <p className="mt-1 text-red-700">
              We encountered an error while loading the Voice Agents dashboard. Please try refreshing the page or contact support if the issue persists.
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview" data-tab="overview" className="flex items-center">
            <Mic className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="agents" data-tab="agents" className="flex items-center">
            <Bot className="h-4 w-4 mr-2" />
            Voice Agents
          </TabsTrigger>
          <TabsTrigger value="calls" data-tab="calls" className="flex items-center">
            <Phone className="h-4 w-4 mr-2" />
            Call History
          </TabsTrigger>
          <TabsTrigger value="analytics" data-tab="analytics" className="flex items-center">
            <BarChart2 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="settings" data-tab="settings" className="flex items-center">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="troubleshooting" data-tab="troubleshooting" className="flex items-center">
            <HelpCircle className="h-4 w-4 mr-2" />
            Troubleshooting
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h2 className="text-lg font-semibold mb-4">LeadWealth.ai Voice Agents</h2>
                <p className="text-gray-600 mb-6">
                  LeadWealth.ai voice agents use advanced AI to engage with potential clients, qualify leads, and schedule appointments automatically.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-2xl font-bold text-primary">3</div>
                    <div className="text-sm text-gray-600">Active Agents</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-2xl font-bold text-primary">24</div>
                    <div className="text-sm text-gray-600">Calls Today</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-2xl font-bold text-primary">92%</div>
                    <div className="text-sm text-gray-600">Success Rate</div>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <h3 className="text-md font-medium">Recent Activity</h3>
                  <button className="text-primary text-sm font-medium">View All</button>
                </div>
                
                <div className="mt-4 space-y-4">
                  {[
                    { agent: 'Sarah', action: 'Scheduled appointment', time: '10 minutes ago', success: true },
                    { agent: 'Michael', action: 'Qualified lead', time: '45 minutes ago', success: true },
                    { agent: 'Alex', action: 'Left voicemail', time: '2 hours ago', success: true }
                  ].map((activity, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                          <Mic className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">{activity.agent}</div>
                          <div className="text-sm text-gray-500">{activity.action}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">{activity.time}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h2 className="text-lg font-semibold mb-4">Performance Overview</h2>
                <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-500">Performance chart will appear here</p>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-1">
              <VoiceAgentStatusWidget className="mb-6" />
              
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
                <div className="space-y-3">
                  <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center">
                    <Bot className="h-5 w-5 text-primary mr-3" />
                    <span>Create New Agent</span>
                  </button>
                  <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center">
                    <Phone className="h-5 w-5 text-primary mr-3" />
                    <span>View Call Logs</span>
                  </button>
                  <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center">
                    <Settings className="h-5 w-5 text-primary mr-3" />
                    <span>Configure Settings</span>
                  </button>
                  <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center">
                    <BarChart2 className="h-5 w-5 text-primary mr-3" />
                    <span>View Analytics</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="agents">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold">Voice Agents</h2>
              <button className="btn btn-primary py-2">
                Create New Agent
              </button>
            </div>
            
            <div className="space-y-4">
              {[
                { name: 'Sarah', role: 'Appointment Scheduler', status: 'active', calls: 156, conversion: '34%' },
                { name: 'Michael', role: 'Lead Qualifier', status: 'active', calls: 203, conversion: '28%' },
                { name: 'Alex', role: 'Follow-up Specialist', status: 'training', calls: 0, conversion: '0%' }
              ].map((agent, index) => (
                <div key={index} className="border rounded-lg p-4 hover:border-primary transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                        <Mic className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-lg">{agent.name}</div>
                        <div className="text-sm text-gray-500">{agent.role}</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        agent.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : agent.status === 'training'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {agent.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="bg-gray-50 rounded p-3">
                      <div className="text-sm text-gray-500">Total Calls</div>
                      <div className="font-semibold">{agent.calls}</div>
                    </div>
                    <div className="bg-gray-50 rounded p-3">
                      <div className="text-sm text-gray-500">Conversion Rate</div>
                      <div className="font-semibold">{agent.conversion}</div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end mt-4 space-x-2">
                    <button className="btn btn-outline py-1 px-3 text-sm">Edit</button>
                    <button className="btn btn-outline py-1 px-3 text-sm">View Calls</button>
                    <button className="btn btn-primary py-1 px-3 text-sm">Configure</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="calls">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold mb-6">Call History</h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outcome</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {[
                    { agent: 'Sarah', phone: '(555) 123-4567', date: '2025-06-07 14:30', duration: '4:32', outcome: 'Appointment Scheduled' },
                    { agent: 'Michael', phone: '(555) 234-5678', date: '2025-06-07 13:15', duration: '3:45', outcome: 'Qualified Lead' },
                    { agent: 'Sarah', phone: '(555) 345-6789', date: '2025-06-07 11:20', duration: '2:18', outcome: 'Voicemail' },
                    { agent: 'Michael', phone: '(555) 456-7890', date: '2025-06-07 10:05', duration: '5:22', outcome: 'Not Interested' },
                    { agent: 'Sarah', phone: '(555) 567-8901', date: '2025-06-06 16:45', duration: '3:56', outcome: 'Appointment Scheduled' }
                  ].map((call, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                            <Mic className="h-4 w-4 text-primary" />
                          </div>
                          <div className="font-medium">{call.agent}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{call.phone}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{call.date}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{call.duration}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          call.outcome === 'Appointment Scheduled' 
                            ? 'bg-green-100 text-green-800' 
                            : call.outcome === 'Qualified Lead'
                            ? 'bg-blue-100 text-blue-800'
                            : call.outcome === 'Voicemail'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {call.outcome}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button className="text-primary hover:text-primary-dark text-sm font-medium">
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="analytics">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Phone className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mt-4">247</h3>
                <p className="text-gray-600">Total Calls</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mt-4">42</h3>
                <p className="text-gray-600">Appointments Set</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BarChart2 className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mt-4">17%</h3>
                <p className="text-gray-600">Conversion Rate</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mt-4">3:42</h3>
                <p className="text-gray-600">Avg Call Duration</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h2 className="text-lg font-semibold mb-4">Call Outcomes</h2>
                <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-500">Call outcomes chart will appear here</p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h2 className="text-lg font-semibold mb-4">Agent Performance</h2>
                <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-500">Agent performance chart will appear here</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h2 className="text-lg font-semibold mb-4">Call Volume Trends</h2>
              <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-500">Call volume trend chart will appear here</p>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="settings">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold mb-6">Voice Agent Settings</h2>
            
            <div className="space-y-6">
              <div className="border-b pb-6">
                <h3 className="text-md font-medium mb-4">API Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LeadWealth.ai API Key
                    </label>
                    <div className="flex">
                      <input
                        type="password"
                        value="••••••••••••••••••••••"
                        disabled
                        className="flex-grow rounded-l-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                      />
                      <button className="bg-primary text-white px-4 py-2 rounded-r-md">
                        Connect
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Connection Status
                    </label>
                    <div className="flex items-center bg-gray-50 p-3 rounded-md">
                      <div className="h-3 w-3 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-sm">Connected</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="border-b pb-6">
                <h3 className="text-md font-medium mb-4">Call Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maximum Calls Per Day
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      defaultValue="100"
                      className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Call Hours
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                        <input
                          type="time"
                          defaultValue="09:00"
                          className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">End Time</label>
                        <input
                          type="time"
                          defaultValue="17:00"
                          className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        defaultChecked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-700">Respect Do Not Call list</span>
                    </label>
                  </div>
                  
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        defaultChecked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-700">Enable call recording</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="border-b pb-6">
                <h3 className="text-md font-medium mb-4">Voice Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Voice
                    </label>
                    <select className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary">
                      <option>Sarah (Female, Professional)</option>
                      <option>Michael (Male, Professional)</option>
                      <option>Alex (Gender Neutral, Professional)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Speech Rate
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="1.5"
                      step="0.1"
                      defaultValue="1"
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Slower</span>
                      <span>Normal</span>
                      <span>Faster</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-md font-medium mb-4">Integration Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CRM Integration
                    </label>
                    <select className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary">
                      <option>Salesforce</option>
                      <option>HubSpot</option>
                      <option>Zoho CRM</option>
                      <option>None</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Calendar Integration
                    </label>
                    <select className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary">
                      <option>Google Calendar</option>
                      <option>Microsoft Outlook</option>
                      <option>Apple Calendar</option>
                      <option>None</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <button className="btn btn-primary">
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="troubleshooting">
          <AIVoiceAgentTroubleshooting />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIVoiceAgentDashboard;